import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import { assertNever, type DemoFixtureIdentity } from 'tio-employee/demo/types';
import type { DemoTaPaymentModelId } from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Fixtures for the "in-progress" Tuition Assistance demo: an active participant
 * with an active eligibility on a launched program template, one active program
 * instance, and one in-progress application with one course. Shapes follow the
 * proven demo push payloads from the TAS getting-started flow.
 *
 * Served through the demo handler for the store-routed TAS pages
 * (tas/getting-started, tas/dashboard-v3, tas/programs, tas/applications/*).
 *
 * Employer/employee names derive from the {@link DemoFixtureIdentity} the
 * registry resolves from the active selection, so the TAS surfaces (dashboard,
 * approval queue) always agree with the session identity — never a hardcoded
 * "Acme Corporation" against a "Lifepoint Health" header.
 *
 * The application/course/company-setting fixtures are shaped by the selected
 * TA payment model (reimbursement / prepayment-CourseCash / deferral), so the
 * classic admin drill-ins (approval page, application show) reflect the model
 * instead of always rendering reimbursement (the PR #8770 seam gap). The
 * drill-ins read the JSON:API MODEL GETTERS, so each branch fabricates exactly
 * what those getters consume — all monetary values in CENTS:
 * - model.paymentMethod        <- fields.PAYMENT_TYPE_FOR_DIRECT_PAY
 * - model.cardPreApprovedTotal <- fields.CARD_PRE_APPROVED_TOTAL (UNsuffixed —
 *   the `…Integer` field family belongs to the OpenSearch queue/list hits,
 *   not the JSON:API model; mixing them up renders $0)
 * - course-cash balances       <- amounts.course_cash.{reserved,used,refunded}
 * - requested/expense rows     <- the linked tas-course's COURSE_* fields
 * Course naming mirrors the reimagined snapshot plane
 * (reimagined-data#getDemoTasData) so both demo planes tell one story.
 */

const DEMO_CODE = 'DEMO_PROGRAM';

const ELIGIBILITY_RESOURCE: JsonApiResource = {
  type: 'tas-eligibility',
  id: 'demo-tas-eligibility-1',
  attributes: { code: DEMO_CODE, state: 'TAS.EligibilityState.ACTIVE' },
};

function buildTemplateResource(programName: string): JsonApiResource {
  return {
    type: 'tas-program-template',
    id: 'demo-tas-template-1',
    attributes: {
      code: DEMO_CODE,
      state: 'TAS.ProgramTemplateState.LAUNCHED',
      fields: {
        PROGRAM_NAME: { values: [programName] },
        PROGRAM_DESCRIPTION: { values: ['Employer-sponsored tuition assistance.'] },
        TYPE: { values: ['TAS.ProgramType.4.2'] },
        DEPENDENT_PROGRAM: { values: [false] },
        MULTIPLE_INSTANCE_PROGRAM: { values: [false] },
        ELIGIBILITY_WAITING_PERIOD: { values: [0] },
      },
      faqs: [],
      customFields: [],
      approverInstructions: [],
      isTrial: false,
    },
    relationships: {
      conditions: { data: [] },
      tasEligibilities: { data: [] },
      tasGroupings: { data: [] },
      tasProgramInstances: { data: [] },
      tasFieldOptions: { data: [] },
      tasGroups: { data: [] },
      // Admin application/template screens read template.company.companySetting.
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

// The approval + admin application-show surfaces walk
// instance.employee(.person/.company/.tasParticipant), so the instance links
// the demo employee; empty hasMany linkage keeps asset/attachment readers on
// a clean empty state instead of crashing on undefined.
const INSTANCE_RESOURCE: JsonApiResource = {
  type: 'tas-program-instance',
  id: 'demo-tas-instance-1',
  attributes: {
    state: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
  },
  relationships: {
    tasProgramTemplate: { data: { type: 'tas-program-template', id: 'demo-tas-template-1' } },
    employee: { data: { type: 'employee', id: 'demo-tas-employee-1' } },
    tasAssets: { data: [] },
    attachments: { data: [] },
  },
};

const PARTICIPANT_RESOURCE: JsonApiResource = {
  type: 'tas-participant',
  id: 'demo-tas-participant-1',
  attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
  relationships: {
    tasEligibilities: { data: [{ type: 'tas-eligibility', id: 'demo-tas-eligibility-1' }] },
    tasGroupings: { data: [] },
  },
};

// --- Payment-model shaping ---------------------------------------------------

// Course expenses in CENTS; they sum to $1,500, matching the limits fixture's
// product usage. Under prepayment the tuition is the CourseCash-covered part
// (CARD_PRE_APPROVED_TOTAL) while books/fees remain reimbursable, so the
// approval page shows the real CourseCash-vs-reimbursement split.
const COURSE_TUITION_CENTS = 120_000; // $1,200
const COURSE_BOOKS_CENTS = 22_500; // $225
const COURSE_FEES_CENTS = 7_500; // $75

// Mirrors the snapshot plane's per-model naming (reimagined-data
// getDemoTasData: 'Coursera / Google (CourseCash)' / 'Riverview LPN School
// (deferral-eligible)'), so the employee reimagined pages and the store-routed
// drill-ins narrate the same demo.
function courseNameFor(paymentModelId: DemoTaPaymentModelId): string {
  switch (paymentModelId) {
    case 'prepayment':
      return 'Google Data Analytics (CourseCash)';
    case 'reimbursement-with-deferral':
      return 'LPN to RN Bridge — Riverview LPN School (deferral-eligible)';
    case 'reimbursement':
      return 'Intro to Data Science';
    default:
      return assertNever(paymentModelId);
  }
}

// The expense table, requested totals, and the CourseCash subtotal on the
// approval page all derive from tas-course COURSE_* fields (fields.REQUESTED_TOTAL
// on the application has no model reader) — without a course every amount
// renders zero. The course's tasApplication back-link is deliberately omitted:
// the inverse is wired when the application's `tasCourses` linkage is pushed,
// which also stays correct when the handler echoes a requested application id.
function buildCourseResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  return {
    type: 'tas-course',
    id: 'demo-tas-course-1',
    attributes: {
      isActive: true,
      customFields: [],
      fields: {
        COURSE_NAME: { values: [courseNameFor(paymentModelId)] },
        COURSE_NUMBER: { values: ['DEMO-101'] },
        COURSE_TUITION: { values: [COURSE_TUITION_CENTS] },
        COURSE_BOOKS: { values: [COURSE_BOOKS_CENTS] },
        COURSE_FEES: { values: [COURSE_FEES_CENTS] },
        COURSE_LABS: { values: [0] },
        COURSE_SOFTWARE: { values: [0] },
        COURSE_CREDIT: { values: [3] },
      },
    },
    relationships: {
      tasAssets: { data: [] },
      attachments: { data: [] },
    },
  };
}

// The admin financial summary's CourseCash section reads the application's
// pcsCards for the card-status chip. CREATED renders "Pending Activation",
// coherent with a pending-approval application. The employee card display
// (stripe-card-display) guards its Stripe issuing-elements setup behind
// card.pcsCompany.providerId, which this resource deliberately omits, so demo
// never reaches the issuing flow or its raw `/pcs-cards/:id/client-token`
// fetch. The tasApplication back-link is inverse-wired from the application.
const PCS_CARD_RESOURCE: JsonApiResource = {
  type: 'pcs-card',
  id: 'demo-pcs-card-1',
  attributes: {
    state: 'PCS.CardState.CREATED',
    program: 'PCS.Program.COURSE_CASH',
    amounts: {
      authorization: {
        approved: 0,
        captured: 0,
        created: 0,
        declined: 0,
        expired: 0,
        requested: 0,
        reversed: 0,
      },
      dispute: { created: 0 },
      program: { approved: COURSE_TUITION_CENTS },
      transaction: { captured: 0, created: 0, refunded: 0 },
    },
  },
  relationships: {
    pcsAuthorizations: { data: [] },
    pcsCardEvents: { data: [] },
    pcsDisputes: { data: [] },
    pcsTransactions: { data: [] },
  },
};

// Every async:false hasMany the approval screens iterate is linked empty —
// an OMITTED linkage materializes as undefined and crashes `.find(...)` /
// `.filter(...)` readers, while an empty one renders the empty state.
function buildApplicationResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  const isPrepayment = paymentModelId === 'prepayment';

  return {
    type: 'tas-application',
    id: 'demo-tas-application-1',
    attributes: {
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      // Nothing is paid pre-approval under ANY payment model. Prepayment
      // differs by the CourseCash fields + card below; deferral differs by
      // withholding payment AFTER approval, which an in-progress application
      // cannot exhibit yet — richer deferral payment states arrive with the
      // 'approved' TAS sub-scenario (#8795).
      paymentState: 'TAS.ApplicationPaymentState.NONE',
      fields: {
        COURSE_NAME: { values: [courseNameFor(paymentModelId)] },
        REQUESTED_TOTAL: { values: [1500] },
        ...(isPrepayment
          ? {
              // model.paymentMethod — flips every CourseCash gate on the
              // approval surfaces.
              PAYMENT_TYPE_FOR_DIRECT_PAY: { values: ['TAS.AssistanceManner.COURSE_CASH'] },
              // model.cardPreApprovedTotal, in CENTS (the surfaces format /100).
              CARD_PRE_APPROVED_TOTAL: { values: [COURSE_TUITION_CENTS] },
            }
          : {}),
      },
      customFields: [],
      // ApplicationAmounts in CENTS, fully formed for EVERY model:
      // stripe-card-display reads amounts.course_cash UNGUARDED, so the block
      // must exist the moment a pcs-card makes that component render.
      amounts: {
        requested: COURSE_TUITION_CENTS + COURSE_BOOKS_CENTS + COURSE_FEES_CENTS,
        approved_for_payment: 0,
        course_cash: { reserved: 0, used: 0, refunded: 0 },
        pending_payment: 0,
        paid: 0,
        refund_requested: 0,
        refund_paid_back: 0,
        refund_forgiven: 0,
        refund_cancelled: 0,
        refund_limit: 0,
        flags: { request_payment_enabled: false, request_repayment_enabled: false },
      },
      // The approval-queue status widgets read the state-matching approval
      // summary (`coursesApproval.possible_reviewers` etc.) without guarding.
      coursesApproval: {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      },
      // The approval surfaces derive submission/approval dates from these logs
      // via `.find(...)` — they must exist (empty is fine; undefined crashes).
      transitionLog: [
        {
          event: 'submit',
          from: 'TAS.ApplicationState.DEFINE_COURSES',
          to: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          timestamp: '2026-05-20T14:00:00Z',
          reason: null,
        },
      ],
      paymentStateLog: [],
      approvedTotalLog: [],
    },
    relationships: {
      tasProgramInstance: { data: { type: 'tas-program-instance', id: 'demo-tas-instance-1' } },
      tasCourses: { data: [{ type: 'tas-course', id: 'demo-tas-course-1' }] },
      tasAssistances: { data: [] },
      tasAssets: { data: [] },
      attachments: { data: [] },
      aiRecommendations: { data: [] },
      pcsCards: { data: isPrepayment ? [{ type: 'pcs-card', id: 'demo-pcs-card-1' }] : [] },
      tags: { data: [] },
    },
  };
}

// company-setting drives several dashboard reads
// (currentEmployee.company.companySetting.tuitionAssistance.*). The direct-pay
// and card-review keys are the employer-level CourseCash gates, mirroring
// buildDemoCompanySettings (career-pathways-demo-data.ts), so the store plane
// and the partner-service blob agree on whether CourseCash UI is enabled.
function buildCompanySettingResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  const isPrepayment = paymentModelId === 'prepayment';
  return {
    type: 'company-setting',
    id: 'demo-tas-companysetting-1',
    attributes: {
      tuitionAssistance: {
        hideAnnualBenefitsCard: false,
        isDependentProgramsEnabled: false,
        isDirectPayEnabled: isPrepayment,
        isCardUsageReviewEnabled: isPrepayment,
      },
    },
  };
}

function buildCompanyResource(employerName: string): JsonApiResource {
  return {
    type: 'company',
    id: 'demo-tas-company-1',
    attributes: { name: employerName, isActive: true },
    relationships: {
      companySetting: { data: { type: 'company-setting', id: 'demo-tas-companysetting-1' } },
    },
  };
}

// Employee findRecord (include tas-participant,company) — the dashboard reads
// employee.tasParticipant to decide whether to run eligibility checks, so the
// participant must be sideloaded for the demo program to surface. Its
// company/company-setting/participant ids are reused by demo/identity-fixtures.ts
// (buildDemoIdentityDoc) for the demo session identity — and its NAMES derive
// from the same identity, so this later push can never rename the session user.
function buildEmployeeResource(identity: DemoFixtureIdentity): JsonApiResource {
  return {
    type: 'employee',
    id: 'demo-tas-employee-1',
    attributes: {
      firstName: identity.firstName,
      lastName: identity.lastName,
      isActive: true,
      // Admin application-details reads customAttributes.raw?... without
      // guarding customAttributes itself.
      customAttributes: {},
      employmentType: 'FULL_TIME',
    },
    relationships: {
      tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

const participantCollection: JsonApiDoc = {
  data: [PARTICIPANT_RESOURCE],
  included: [ELIGIBILITY_RESOURCE],
};

// Empty OpenSearch result — a valid "enrolled, no applications yet" state.
// executeSearchRequest is a store.request builder (POST, SkipCache), so the
// demo HANDLER intercepts it (not the fetch interceptor); callers read .content.
const EMPTY_SEARCH = {
  took: 0,
  timedOut: false,
  hits: { total: { value: 0, relation: 'eq' }, maxScore: 0, hits: [] },
  aggregations: {},
};

// --- Admin TAS approval queue (OpenSearch /api/v2/search, model-type
// TasProgramInstance) -------------------------------------------------------
// The admin approval queue (routes/.../admin/tas/approval-queue) POSTs an
// OpenSearch query and renders `OpenSearchResponseBuilder.from(res).toPaginatedResult()`.
// Each hit's `_source` is a TasProgramInstance doc; the queue row reads
// employeeNameKeyword / employeePayrollIdKeyword / companyName / state / dates.
interface QueueAppSource {
  id: string;
  employeeName: string;
  payrollId: string;
  employeeId: number;
  personId: number;
  state: string;
  submitted: string;
  priority: string;
}

function queueHit(app: QueueAppSource, employerName: string, programName: string) {
  return {
    _index: 'tas_program_instances',
    _id: app.id,
    _score: 1,
    _source: {
      id: app.id,
      recordType: 'application',
      companyId: 'demo-tas-company-1',
      companyName: employerName,
      employeeId: app.employeeId,
      personId: app.personId,
      employeeNameKeyword: app.employeeName,
      employeePayrollIdKeyword: app.payrollId,
      tasApplicationIdKeyword: `${app.id}-application`,
      tasProgramInstanceIdKeyword: `${app.id}-instance`,
      tasApplicationStateKeyword: app.state,
      tasApplicationCreatedAtDate: app.submitted,
      tasApplicationAttentionPriorityDate: app.priority,
      tasApplicationLatestSubmissionTs: `${app.submitted}T14:00:00Z`,
      tasApplicationLatestStateChangeAtDate: app.submitted,
      tasProgramTemplateFieldProgramNameKeyword: programName,
    },
  };
}

const APPROVAL_QUEUE_APPS: QueueAppSource[] = [
  {
    id: 'demo-queue-1',
    employeeName: 'Jordan Lee',
    payrollId: 'EMP-1001',
    employeeId: 1001,
    personId: 2001,
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    submitted: '2026-05-20',
    priority: '2026-05-21',
  },
  {
    id: 'demo-queue-2',
    employeeName: 'Priya Natarajan',
    payrollId: 'EMP-1042',
    employeeId: 1042,
    personId: 2042,
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    submitted: '2026-05-22',
    priority: '2026-05-23',
  },
  {
    id: 'demo-queue-3',
    employeeName: 'Marcus Brown',
    payrollId: 'EMP-1088',
    employeeId: 1088,
    personId: 2088,
    state: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    submitted: '2026-05-24',
    priority: '2026-05-25',
  },
];

export function buildInProgressTasMatchers(
  identity: DemoFixtureIdentity,
  paymentModelId: DemoTaPaymentModelId
): MatcherFixture[] {
  const programName = `${identity.employerName} Tuition Assistance`;
  const templateResource = buildTemplateResource(programName);
  const companyResource = buildCompanyResource(identity.employerName);
  const companySettingResource = buildCompanySettingResource(paymentModelId);
  const employeeResource = buildEmployeeResource(identity);
  const applicationResource = buildApplicationResource(paymentModelId);
  const courseResource = buildCourseResource(paymentModelId);

  const employeeDoc: JsonApiDoc = {
    data: employeeResource,
    included: [PARTICIPANT_RESOURCE, ELIGIBILITY_RESOURCE, companyResource, companySettingResource],
  };
  const templateCollection: JsonApiDoc = {
    data: [templateResource],
    included: [companyResource, companySettingResource],
  };
  const templateDoc: JsonApiDoc = {
    data: templateResource,
    included: [companyResource, companySettingResource],
  };
  // The instance links the demo employee, so every doc that sideloads it also
  // sideloads the employee graph (participant/eligibility/company/setting) —
  // async:false relationships resolve empty unless included.
  const employeeGraph = [
    employeeResource,
    PARTICIPANT_RESOURCE,
    ELIGIBILITY_RESOURCE,
    companyResource,
    companySettingResource,
  ];
  const instanceCollection: JsonApiDoc = {
    data: [INSTANCE_RESOURCE],
    included: [templateResource, ...employeeGraph],
  };
  const instanceDoc: JsonApiDoc = {
    data: INSTANCE_RESOURCE,
    included: [templateResource, ...employeeGraph],
  };
  // Sideload the instance AND its template so async:false
  // tasProgramInstance.tasProgramTemplate resolves on the application show/confirmation routes.
  // The course (and the CourseCash card under prepayment) ride along for the
  // same reason — the expense table and card chip read them async:false.
  const applicationGraph = [
    courseResource,
    ...(paymentModelId === 'prepayment' ? [PCS_CARD_RESOURCE] : []),
    INSTANCE_RESOURCE,
    templateResource,
    ...employeeGraph,
  ];
  const applicationDoc: JsonApiDoc = {
    data: applicationResource,
    included: applicationGraph,
  };
  // Application list reads (e.g. the employee's applications on the admin
  // drill-in) get the same in-progress application.
  const applicationCollection: JsonApiDoc = {
    data: [applicationResource],
    included: applicationGraph,
  };

  const approvalQueueSearch = {
    took: 0,
    timedOut: false,
    hits: {
      total: { value: APPROVAL_QUEUE_APPS.length, relation: 'eq' },
      maxScore: 1,
      hits: APPROVAL_QUEUE_APPS.map((app) => queueHit(app, identity.employerName, programName)),
    },
    aggregations: {},
  };

  return [
    // The admin approval-queue search (model-type TasProgramInstance) gets demo
    // applications; this MUST precede the generic empty-search matcher below
    // (first-match-wins), which still serves every other search (e.g. reporting).
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: (url) => /\/api\/v2\/search(?:\?|$)/.test(url),
        bodyMatches: (body) =>
          (body as { 'model-type'?: string } | undefined)?.['model-type'] === 'TasProgramInstance',
      },
      content: approvalQueueSearch,
    },
    // Base /api/v2/search POSTs only — never the /api/v2/search/* metadata
    // subroutes (mapping/fields/...), matching the registry's precise pattern.
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: (url) => /\/api\/v2\/search(?:\?|$)/.test(url),
      },
      content: EMPTY_SEARCH,
    },
    { matcher: { kind: 'op', op: 'findRecord', type: 'employee' }, docs: [employeeDoc] },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-participant' },
      docs: [participantCollection],
    },
    // The dashboard runs a SECOND tas-program-template query for trial templates
    // (admins only). It must NOT return the launched demo program, or the eligible
    // list shows a duplicate card. This filter-specific matcher precedes the
    // generic one below (first match wins) and serves an empty collection.
    {
      matcher: {
        kind: 'op',
        op: 'query',
        type: 'tas-program-template',
        where: (query) => (query?.filter as { isTrial?: boolean } | undefined)?.isTrial === true,
      },
      docs: [{ data: [] }],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-program-template' },
      docs: [templateCollection],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-template' },
      docs: [templateDoc],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-program-instance' },
      docs: [instanceCollection],
    },
    // tas/programs loads its primary model via findRecord('tas-program-instance', ...).
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-instance' },
      docs: [instanceDoc],
    },
    { matcher: { kind: 'op', op: 'query', type: 'tas-group' }, docs: [{ data: [] }] },
    { matcher: { kind: 'op', op: 'findRecord', type: 'tas-application' }, docs: [applicationDoc] },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-application' },
      docs: [applicationCollection],
    },
  ];
}

// --- Raw-fetch (seam B) matchers -------------------------------------------
// The TAS dashboard reaches these directly via fetch (bypassing the store):
// limits status and the tas-participant eligibility endpoints. (OpenSearch is
// NOT here — executeSearchRequest is a store.request builder, so it is served
// by the demo handler via the '/api/v2/search' builder matcher above.)

const limitsDetail = (limit: number | null, usage: number) => ({
  annual_limit: limit,
  annual_usage: usage,
  available: limit === null ? null : limit - usage,
  hard: limit !== null,
  limit,
  one_month_limit: null,
  one_month_usage: 0,
  six_month_limit: null,
  six_month_usage: 0,
  taxable_limit: null,
  three_month_limit: null,
  three_month_usage: 0,
  usage,
});

const DEMO_LIMITS = {
  data: [
    {
      annual_start_date: '2026-01-01',
      annual_end_date: '2026-12-31',
      available: 3750,
      available_with_override: 3750,
      details: {
        company: limitsDetail(null, 0),
        product: limitsDetail(5250, 1500),
        batch: limitsDetail(null, 0),
        scheme: limitsDetail(null, 0),
      },
      employee_id: 'demo-tas-employee-1',
      hard_limit: 'product_annual',
      limit_capacity: 'TAS.LimitCapacity.MONETARY',
      product: 'TA',
      scheme: null,
      soft_limit: 'none',
    },
  ],
};

export const IN_PROGRESS_TAS_FETCH_MATCHERS: FetchMatcher[] = [
  { urlIncludes: '/dynamic-eligibility', json: { [DEMO_CODE]: true } },
  { urlIncludes: '/periods-of-eligibility', json: { [DEMO_CODE]: [['2024-01-01', '2027-12-31']] } },
  { urlIncludes: '/api/v2/limits/status', json: DEMO_LIMITS },
  // The approval screens load each application's audit trail through the
  // TasApplicationAdapter's raw `getHistory` ajax (seam B, demoJsonFor).
  // `loadHistory` assigns `response.data` to `TasApplication.history`
  // ({ assistance_history, application_history }) — empty arrays render a
  // clean, empty activity timeline.
  {
    urlMatches: (url) => /\/tas-applications\/[^/]+\/history(?:\?|$)/.test(url),
    method: 'GET',
    json: { data: { assistance_history: [], application_history: [] } },
  },
];
